'use client';

import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Navbar } from '../components/layout/navbar';
import { Footer } from '../components/layout/footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import {
  Bell,
  Check,
  FileText,
  MessageSquare,
  Heart,
  User,
  AtSign,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthStore } from '../stores';
import { useGetUserById } from '../hooks/use-user';
import { useNotifications } from '../hooks/use-notifications';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { walletAddress, isAuthenticated } = useAuthStore();

  // Get current user
  const { data: user, isLoading: isLoadingUser } = useGetUserById(
    walletAddress
  );

  // Get notifications using the user's ID
  const {
    notifications = [],
    isLoading,
    markAsRead,
    markAllAsRead,
    getUnreadCount,
  } = useNotifications(user?.oid);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, navigate]);

  const handleMarkAllAsRead = () => {
    if (markAllAsRead) markAllAsRead();
  };

  const handleMarkAsRead = (id) => {
    if (markAsRead) markAsRead(id);
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like':
        return <Heart className="h-5 w-5 text-red-500" />;
      case 'comment':
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case 'follow':
        return <User className="h-5 w-5 text-purple-500" />;
      case 'mention':
        return <AtSign className="h-5 w-5 text-green-500" />;
      case 'article':
        return <FileText className="h-5 w-5 text-amber-500" />;
      case 'forum':
        return <MessageSquare className="h-5 w-5 text-indigo-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  // Get notification source badge
  const getSourceBadge = (source) => {
    if (source === 'blog') {
      return (
        <Badge
          variant="outline"
          className="ml-2 border-amber-500 text-amber-600 bg-amber-50"
        >
          <FileText className="h-3 w-3 mr-1" />
          Blog
        </Badge>
      );
    } else if (source === 'forum') {
      return (
        <Badge
          variant="outline"
          className="ml-2 border-indigo-500 text-indigo-600 bg-indigo-50"
        >
          <MessageSquare className="h-3 w-3 mr-1" />
          Forum
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="flex min-h-screen flex-col bg-black text-white">
      {/* Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      <Navbar />
      <main className="flex-1 container px-4 py-8 md:px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold">Notifications</h1>
            {notifications.some((n) => !n.read) && (
              <Button variant="outline" onClick={handleMarkAllAsRead}>
                <Check className="h-4 w-4 mr-2" />
                Mark all as read
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-4 border rounded-lg"
                >
                  <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded animate-pulse mb-2 w-3/4" />
                    <div className="h-3 bg-muted rounded animate-pulse mb-2 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length > 0 ? (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex items-start gap-4 p-4 border rounded-lg ${
                    !notification.read
                      ? 'bg-gray-50 border-gray-300'
                      : 'bg-white'
                  }`}
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                      {getNotificationIcon(notification.type)}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">
                          {notification.content}
                          {getSourceBadge(notification.source)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(notification.date), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleMarkAsRead(notification.id)}
                        >
                          <Check className="h-4 w-4" />
                          <span className="sr-only">Mark as read</span>
                        </Button>
                      )}
                    </div>
                    {notification.actionUrl && (
                      <div className="mt-2">
                        <Link to={notification.actionUrl}>
                          <Button variant="outline" size="sm">
                            View
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border rounded-lg">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Bell className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">No notifications</h3>
              <p className="text-muted-foreground mb-4">
                You're all caught up! Check back later for new notifications.
              </p>
              <Link to="/home">
                <Button>Browse articles</Button>
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
